import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mercy_user") || "null"); } catch { return null; }
  });
  const [checking, setChecking] = useState(true);
  // Ref so the silent retry timer survives re-renders without re-triggering effects.
  const retryTimerRef = useRef(null);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("mercy_token");
    if (!token) { setUser(null); setChecking(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("mercy_user", JSON.stringify(data));
      setChecking(false);
    } catch (err) {
      // CRITICAL UX FIX: only treat a 401 as "actually logged out". Network
      // errors / 5xx / timeouts (e.g. backend pod restart, mobile blip) used
      // to wipe the session and kick the user to /login — root cause of
      // the "intermittent can't-log-in 2-3×/month, everyone at once" reports.
      // Now: keep the cached user, retry quietly in the background.
      const status = err?.response?.status;
      if (status === 401) {
        localStorage.removeItem("mercy_token");
        localStorage.removeItem("mercy_user");
        setUser(null);
        setChecking(false);
        return;
      }
      // Transient failure — keep cached user (already set from localStorage),
      // try again in 8 s. Don't change `user` or `checking` (we already
      // restored from cache at mount).
      setChecking(false);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      retryTimerRef.current = setTimeout(() => { refresh(); }, 8000);
    }
  }, []);

  useEffect(() => {
    refresh();
    return () => { if (retryTimerRef.current) clearTimeout(retryTimerRef.current); };
  }, [refresh]);

  const login = async (username, password) => {
    // Small built-in retry: production sometimes returns a transient 5xx /
    // network error during pod cold-start. One quiet retry fixes most cases
    // before the user even sees "invalid". Wrong password still 401s on the
    // first attempt and is surfaced immediately.
    let lastErr;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await api.post("/auth/login", { username, password });
        localStorage.setItem("mercy_token", data.token);
        localStorage.setItem("mercy_user", JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        // 4xx (invalid creds, disabled, bad request) — don't retry, surface to user
        if (status && status >= 400 && status < 500) throw err;
        // 5xx / no response (network) — wait and retry once
        await new Promise((r) => setTimeout(r, 1200));
      }
    }
    throw lastErr;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("mercy_token");
    localStorage.removeItem("mercy_user");
    // Clear per-user FitLah cache so the next user doesn't see this one's data
    localStorage.removeItem("renewv2");
    localStorage.removeItem("renewv2_uid");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, checking, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
